import { useRef, useState } from 'react';
import { MAX_PREMIOS, novoPremio, ordinal } from '../utils';
import type { PremioDraft } from '../types';

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 5 * 1024 * 1024;

interface BlocoPremiosProps {
  premios: PremioDraft[];
  onChange: (premios: PremioDraft[]) => void;
}

function FotoPremio({
  premio,
  aoAtualizar,
}: {
  premio: PremioDraft;
  aoAtualizar: (patch: Partial<PremioDraft>) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);

  function aoEscolher(arquivo: File | null) {
    setErro(null);
    if (!arquivo) return;
    if (!EXTENSOES_OK.includes(arquivo.type)) {
      setErro('Use jpg, png ou webp.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX) {
      setErro('Imagem acima de 5 MB.');
      return;
    }
    const url = URL.createObjectURL(arquivo);
    aoAtualizar({ foto: arquivo, fotoUrl: url });
  }

  function limpar() {
    setErro(null);
    if (premio.fotoUrl) URL.revokeObjectURL(premio.fotoUrl);
    aoAtualizar({ foto: null, fotoUrl: null });
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="sr-only"
        onChange={(e) => aoEscolher(e.target.files?.[0] ?? null)}
      />
      <div className="flex items-center gap-2">
        {premio.fotoUrl ? (
          <div className="relative">
            <img
              src={premio.fotoUrl}
              alt="Foto do prêmio"
              className="h-12 w-12 rounded-md border border-secondary-200 object-cover"
            />
            <button
              type="button"
              onClick={limpar}
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-secondary-600 text-white hover:bg-secondary-700"
              title="Remover foto"
            >
              <i className="ri-close-line text-xs" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-md border border-dashed border-secondary-300 bg-white text-secondary-400 transition-colors hover:border-primary-400 hover:text-primary-500"
            title="Adicionar foto do prêmio"
          >
            <i className="ri-image-add-line text-lg" />
          </button>
        )}
        <span className="text-xs text-secondary-400">Foto (opcional)</span>
      </div>
      {erro && <p className="mt-1 text-xs text-danger-600">{erro}</p>}
    </div>
  );
}

export default function BlocoPremios({ premios, onChange }: BlocoPremiosProps) {
  function atualizar(chave: string, patch: Partial<PremioDraft>) {
    onChange(premios.map((p) => (p.chave === chave ? { ...p, ...patch } : p)));
  }

  function remover(chave: string) {
    if (premios.length <= 1) return;
    const alvo = premios.find((p) => p.chave === chave);
    if (alvo?.fotoUrl) URL.revokeObjectURL(alvo.fotoUrl);
    onChange(premios.filter((p) => p.chave !== chave));
  }

  function adicionar() {
    if (premios.length >= MAX_PREMIOS) return;
    onChange([...premios, novoPremio()]);
  }

  return (
    <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
      <div className="h-[10px] w-full bg-primary-500" />
      <div className="p-6">
        <h2 className="text-base font-medium text-foreground-950">Os prêmios</h2>
        <p className="mt-0.5 text-sm text-secondary-500">
          O que as pessoas podem ganhar. O primeiro é o prêmio principal.
        </p>

        <div className="mt-4 space-y-3">
          {premios.map((p, i) => (
            <div
              key={p.chave}
              className="rounded-lg border border-secondary-200 bg-background-50 p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700">
                  {ordinal(i + 1)}
                </span>
                <button
                  type="button"
                  onClick={() => remover(p.chave)}
                  disabled={premios.length <= 1}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-secondary-400 transition-colors hover:bg-secondary-100 hover:text-danger-600 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Remover prêmio"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-foreground-800">
                  O que é o prêmio
                </label>
                <input
                  type="text"
                  value={p.desc}
                  onChange={(e) => atualizar(p.chave, { desc: e.target.value })}
                  placeholder="Ex.: Cesta básica completa"
                  className="mt-1 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
                />
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-foreground-800">
                  Quem doou (opcional)
                </label>
                <input
                  type="text"
                  value={p.doou}
                  onChange={(e) => atualizar(p.chave, { doou: e.target.value })}
                  placeholder="Ex.: Padaria Pão Nosso"
                  className="mt-1 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
                />
              </div>

              <div className="mt-3">
                <FotoPremio premio={p} aoAtualizar={(patch) => atualizar(p.chave, patch)} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={adicionar}
            disabled={premios.length >= MAX_PREMIOS}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-white px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-add-line text-lg" />
            Adicionar prêmio
          </button>
          <p className="text-xs text-secondary-400">
            Cada prêmio terá o seu próprio número apurado.
          </p>
        </div>
      </div>
    </section>
  );
}